/**
 * AgendaEventModal — criar/editar um agendamento da Agenda.
 *
 * O evento é uma `activities` com inicio_at (migration 034), então o vínculo
 * escolhido aqui (lead ou negócio) é o mesmo que alimenta a aba "Atividades" do
 * LeadView/DealPanel — nada precisa ser replicado depois.
 *
 * Visual: modal centrado padrão do módulo (fc-modal-*, ../financeiro/contas.css).
 *
 * Props:
 *   onClose()        — fecha sem salvar
 *   onSaved(evento)  — criou/editou (o chamador recarrega a lista)
 *   onDeleted(id)    — excluiu (só aparece em edição)
 *   event            — evento existente (modo edição); ausente = criação
 *   preset           — { contact_id, contact_nome, deal_id, deal_titulo } quando
 *                      aberto de dentro de um lead/negócio: o vínculo vem travado
 *   defaultStart     — Date sugerida (clique numa célula do grid)
 *   members          — usuários da org p/ o seletor de responsável (opcional;
 *                      busca sozinho se não vier)
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  agCreateEvent, agUpdateEvent, agDeleteEvent, agSearchVinculos, getCrmMembers,
} from '../../db/api.js'
import '../financeiro/contas.css'
import './agenda.css'

const TIPOS = [
  { id: 'evento', label: 'Reunião' },
  { id: 'follow_up', label: 'Follow-up' },
  { id: 'tarefa', label: 'Tarefa' },
]

const pad = (n) => String(n).padStart(2, '0')

const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`

/** 'yyyy-mm-dd' + 'HH:MM' no fuso do navegador → ISO 8601 (UTC). */
function toIso(dateStr, timeStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number)
  const [hh, mm] = String(timeStr || '00:00').split(':').map(Number)
  const dt = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0)
  return isNaN(dt) ? null : dt.toISOString()
}

/** Soma minutos a 'HH:MM' (usado pra manter a duração ao mover o início). */
function addMinutes(timeStr, minutes) {
  const [hh, mm] = String(timeStr || '00:00').split(':').map(Number)
  const total = ((hh * 60 + mm + minutes) % 1440 + 1440) % 1440
  return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`
}

export default function AgendaEventModal({
  onClose, onSaved, onDeleted, event = null, preset = null, defaultStart = null,
  members: membersProp = null,
}) {
  const { user } = useApp()
  const navigate = useNavigate()
  const editing = !!event?.id

  const inicio = event?.inicio_at ? new Date(event.inicio_at) : (defaultStart || new Date())
  const fim = event?.fim_at ? new Date(event.fim_at) : new Date(inicio.getTime() + 3600000)

  const [titulo, setTitulo] = useState(event?.titulo || '')
  const [tipo, setTipo] = useState(event?.tipo || 'evento')
  const [data, setData] = useState(toDateInput(inicio))
  const [horaInicio, setHoraInicio] = useState(toTimeInput(inicio))
  const [horaFim, setHoraFim] = useState(toTimeInput(fim))
  const [diaInteiro, setDiaInteiro] = useState(!!event?.dia_inteiro)
  const [local, setLocal] = useState(event?.local || '')
  const [link, setLink] = useState(event?.link_reuniao || '')
  const [descricao, setDescricao] = useState(event?.descricao || '')
  const [responsavel, setResponsavel] = useState(event?.responsavel_id || user?.id || '')

  const [members, setMembers] = useState(membersProp || [])
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  // Vínculo: travado quando veio de dentro de um lead/negócio (preset).
  const travado = !!(preset?.contact_id || preset?.deal_id)
  const [vinculo, setVinculo] = useState(() => {
    if (preset?.deal_id) return { kind: 'deal', id: preset.deal_id, label: preset.deal_titulo || 'Negócio' }
    if (preset?.contact_id) return { kind: 'contact', id: preset.contact_id, label: preset.contact_nome || 'Lead' }
    if (event?.deal_id) return { kind: 'deal', id: event.deal_id, label: event.deal_titulo || 'Negócio' }
    if (event?.contact_id) return { kind: 'contact', id: event.contact_id, label: event.contact_nome || 'Lead' }
    return null
  })
  const [busca, setBusca] = useState('')
  const [resultados, setResultados] = useState(null)   // null = nenhuma busca feita
  const buscaTimer = useRef(null)

  useEffect(() => {
    if (membersProp) return
    let alive = true
    getCrmMembers()
      .then((ms) => { if (alive) setMembers(ms || []) })
      .catch(() => { /* sem lista: o seletor cai no usuário logado */ })
    return () => { alive = false }
  }, [membersProp])

  // Busca de lead/negócio com debounce — evita uma request por tecla.
  useEffect(() => {
    if (travado || vinculo) return undefined
    const termo = busca.trim()
    if (termo.length < 2) { setResultados(null); return undefined }
    clearTimeout(buscaTimer.current)
    buscaTimer.current = setTimeout(() => {
      agSearchVinculos(termo)
        .then((r) => setResultados(r))
        .catch(() => setResultados({ contacts: [], deals: [] }))
    }, 300)
    return () => clearTimeout(buscaTimer.current)
  }, [busca, travado, vinculo])

  const opcoes = useMemo(() => {
    if (!resultados) return []
    return [
      ...(resultados.contacts || []).map((c) => ({
        kind: 'contact', id: c.id, label: c.nome || c.email || 'Lead', sub: c.empresa || c.email || '',
      })),
      ...(resultados.deals || []).map((d) => ({
        kind: 'deal', id: d.id, label: d.titulo || 'Negócio', sub: d.pipeline_nome || '',
      })),
    ]
  }, [resultados])

  // Mover o início arrasta o fim junto, preservando a duração — é o que a
  // pessoa espera ao corrigir só o horário de começo.
  const mudarInicio = (valor) => {
    const [h0, m0] = horaInicio.split(':').map(Number)
    const [h1, m1] = horaFim.split(':').map(Number)
    const dur = (h1 * 60 + m1) - (h0 * 60 + m0)
    setHoraInicio(valor)
    if (dur > 0) setHoraFim(addMinutes(valor, dur))
  }

  const salvar = async () => {
    if (saving) return
    if (!titulo.trim()) { setErr('Título é obrigatório.'); return }
    const inicioIso = diaInteiro ? toIso(data, '00:00') : toIso(data, horaInicio)
    const fimIso = diaInteiro ? toIso(data, '23:59') : toIso(data, horaFim)
    if (!inicioIso || !fimIso) { setErr('Data ou horário inválido.'); return }
    if (new Date(fimIso) <= new Date(inicioIso)) {
      setErr('O horário de término precisa ser depois do início.')
      return
    }

    const payload = {
      titulo: titulo.trim(),
      tipo,
      inicio_at: inicioIso,
      fim_at: fimIso,
      dia_inteiro: diaInteiro,
      local: local.trim() || null,
      link_reuniao: link.trim() || null,
      descricao: descricao.trim() || null,
      responsavel_id: responsavel || null,
      contact_id: vinculo?.kind === 'contact' ? vinculo.id : null,
      deal_id: vinculo?.kind === 'deal' ? vinculo.id : null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    }

    setSaving(true); setErr(null)
    try {
      const salvo = editing
        ? await agUpdateEvent(event.id, payload)
        : await agCreateEvent(payload)
      onSaved?.(salvo)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  const excluir = async () => {
    if (saving || !editing) return
    setSaving(true); setErr(null)
    try {
      await agDeleteEvent(event.id)
      onDeleted?.(event.id)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal ag-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">{editing ? 'Editar agendamento' : 'Novo agendamento'}</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Título<i>*</i></label>
            <input
              className="fc-input" value={titulo} autoFocus
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex.: Diagnóstico comercial"
            />
          </div>

          <div className="fc-field">
            <label className="fc-label">Tipo</label>
            <div className="ag-chips">
              {TIPOS.map((t) => (
                <button
                  key={t.id} type="button"
                  className={`ag-chip${tipo === t.id ? ' active' : ''}`}
                  onClick={() => setTipo(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ag-grid-2">
            <div className="fc-field">
              <label className="fc-label">Data<i>*</i></label>
              <input className="fc-input" type="date" value={data} onChange={(e) => setData(e.target.value)} />
            </div>
            <div className="fc-field">
              <label className="fc-label">Horário</label>
              <div className="ag-time-row">
                <input
                  className="fc-input" type="time" value={horaInicio} disabled={diaInteiro}
                  onChange={(e) => mudarInicio(e.target.value)}
                />
                <span className="ag-time-sep">até</span>
                <input
                  className="fc-input" type="time" value={horaFim} disabled={diaInteiro}
                  onChange={(e) => setHoraFim(e.target.value)}
                />
              </div>
            </div>
          </div>

          <label className="ag-check">
            <input type="checkbox" checked={diaInteiro} onChange={(e) => setDiaInteiro(e.target.checked)} />
            <span>Dia inteiro</span>
          </label>

          <div className="fc-field">
            <label className="fc-label">Responsável</label>
            <select className="fc-select" value={responsavel || ''} onChange={(e) => setResponsavel(e.target.value)}>
              <option value="">— sem responsável —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.nome || m.full_name || m.email}</option>
              ))}
            </select>
          </div>

          <div className="fc-field">
            <label className="fc-label">Vincular a</label>
            {vinculo ? (
              <div className="ag-vinculo">
                <Icon n={vinculo.kind === 'deal' ? 'sales' : 'leads'} s={14} />
                <span className="ag-vinculo-label">{vinculo.label}</span>
                <button
                  type="button" className="ag-vinculo-open"
                  title={vinculo.kind === 'deal' ? 'Abrir negócio no Comercial' : 'Abrir lead no Comercial'}
                  onClick={() => {
                    onClose?.()
                    if (vinculo.kind === 'deal') {
                      navigate('/comercial/pipelines', { state: { openDealId: vinculo.id } })
                    } else {
                      navigate(`/comercial/leads/${vinculo.id}`)
                    }
                  }}
                >
                  <Icon n="external" s={13} sw={2} />
                </button>
                {!travado && (
                  <button type="button" className="ag-vinculo-x" onClick={() => { setVinculo(null); setBusca('') }}>
                    <Icon n="x" s={13} sw={2} />
                  </button>
                )}
              </div>
            ) : (
              <>
                <input
                  className="fc-input" value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar lead ou negócio…"
                />
                {opcoes.length > 0 && (
                  <div className="ag-suggest">
                    {opcoes.map((o) => (
                      <button
                        key={`${o.kind}-${o.id}`} type="button" className="ag-suggest-item"
                        onClick={() => { setVinculo(o); setBusca(''); setResultados(null) }}
                      >
                        <Icon n={o.kind === 'deal' ? 'sales' : 'leads'} s={13} />
                        <span className="ag-suggest-label">{o.label}</span>
                        {o.sub && <span className="ag-suggest-sub">{o.sub}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {resultados && !opcoes.length && (
                  <div className="ag-suggest-empty">Nenhum lead ou negócio encontrado.</div>
                )}
              </>
            )}
          </div>

          <div className="ag-grid-2">
            <div className="fc-field">
              <label className="fc-label">Local</label>
              <input
                className="fc-input" value={local} onChange={(e) => setLocal(e.target.value)}
                placeholder="Ex.: Escritório / Cliente"
              />
            </div>
            <div className="fc-field">
              <label className="fc-label">Link da reunião</label>
              <input
                className="fc-input" value={link} onChange={(e) => setLink(e.target.value)}
                placeholder="https://meet.google.com/…"
              />
            </div>
          </div>

          <div className="fc-field">
            <label className="fc-label">Descrição</label>
            <textarea
              className="fc-input ag-textarea" value={descricao} rows={3}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Pauta, contexto, o que preparar…"
            />
          </div>
        </div>

        <div className="fc-modal-foot">
          {editing && (
            <button className="fc-ctrl ag-danger" onClick={excluir} disabled={saving}>
              <Icon n="trash" s={14} /> Excluir
            </button>
          )}
          <div className="ag-foot-right">
            <button className="fc-ctrl" onClick={onClose} disabled={saving}>Cancelar</button>
            <button className="fc-ctrl primary" onClick={salvar} disabled={saving}>
              {saving ? 'Salvando…' : (editing ? 'Salvar' : 'Agendar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
